import React from 'react';
import { motion } from 'motion/react';
import { TrashIcon } from '@/components/icons';

interface FloatingGuestSelectorProps {
  guestCount: number;
  onGuestCountChange: (count: number) => void;
  isLoadingCarts?: boolean;
  hasCartItemForEBC?: boolean;
  maxGuests?: number | null;
  allowZeroGuests?: boolean;
  // Kept these props optional so existing calls don't break, even if unused
  basePrice?: number;
  isTableBooking?: boolean;
  itemType?: string;
  tableSize?: number | null;
  currency?: string;
}

const FloatingGuestSelector: React.FC<FloatingGuestSelectorProps> = ({
  guestCount,
  onGuestCountChange,
  isLoadingCarts = false,
  hasCartItemForEBC = false,
  maxGuests = null,
  allowZeroGuests = false,
}) => {
  return (
    <div className="fixed bottom-8 left-1/2 transform -translate-x-1/2 z-50">
      <motion.div
        initial={{ opacity: 0, y: 20, scale: 0.9 }}
        animate={{ opacity: 1, y: 0, scale: 1 }}
        transition={{ 
          duration: 0.6, 
          delay: 0.5,
          type: "spring",
          stiffness: 100,
          damping: 15
        }}
        className="bg-black/90 backdrop-blur-xl border border-gray-600/50 rounded-2xl px-8 py-4 flex items-center space-x-6 shadow-2xl"
      >
        {/* Guest Label */}
        <div className="flex items-center">
          <span className="text-sm font-medium text-gray-300">Guests</span>
        </div>

        {/* Decrement Button */}
        <motion.button
          onClick={() => {
            const minCount = allowZeroGuests ? 0 : (hasCartItemForEBC ? 0 : 1);
            onGuestCountChange(Math.max(minCount, guestCount - 1));
          }}
          disabled={guestCount <= (allowZeroGuests ? 0 : (hasCartItemForEBC ? 0 : 1)) || isLoadingCarts}
          whileHover={{ scale: isLoadingCarts ? 1 : 1.1 }}
          whileTap={{ scale: isLoadingCarts ? 1 : 0.95 }}
          className="w-10 h-10 rounded-full bg-gray-700/80 hover:bg-gray-600 disabled:opacity-30 disabled:cursor-not-allowed flex items-center justify-center transition-all duration-200 border border-gray-600/50"
        >
          {isLoadingCarts ? (
            <div className="w-3.5 h-3.5 border-2 border-gray-400/30 border-t-transparent rounded-full animate-spin"></div>
          ) : hasCartItemForEBC && guestCount === 1 ? (
            <TrashIcon className="w-5 h-5 text-gray-300" />
          ) : (
            <svg className="w-5 h-5 text-gray-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 12H4" />
            </svg>
          )}
        </motion.button>
        
        {/* Guest Count Display */}
        <motion.div 
          key={guestCount}
          initial={{ scale: 1.2, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          transition={{ duration: 0.2 }}
          className="text-center min-w-[4rem]"
        >
          <div className="text-2xl font-bold text-white">{guestCount}</div>
          <div className="text-xs text-gray-400 font-medium">
            {guestCount === 1 ? 'Guest' : 'Guests'}
          </div>
        </motion.div>
        
        {/* Increment Button */}
        <motion.button
          onClick={() => onGuestCountChange(guestCount + 1)}
          disabled={isLoadingCarts || (maxGuests !== null && guestCount >= maxGuests)}
          whileHover={{ scale: isLoadingCarts || (maxGuests !== null && guestCount >= maxGuests) ? 1 : 1.1 }}
          whileTap={{ scale: isLoadingCarts || (maxGuests !== null && guestCount >= maxGuests) ? 1 : 0.95 }}
          className="w-10 h-10 rounded-full bg-blue-600 hover:bg-blue-500 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center transition-all duration-200 border border-blue-500/50 shadow-lg shadow-blue-600/25"
          title={maxGuests !== null && guestCount >= maxGuests ? `Maximum ${maxGuests} guests for this table` : undefined}
        >
          {isLoadingCarts ? (
            <div className="w-3.5 h-3.5 border-2 border-white/30 border-t-transparent rounded-full animate-spin"></div>
          ) : (
            <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
            </svg>
          )}
        </motion.button>
      </motion.div>
    </div>
  );
};

export default FloatingGuestSelector;
